import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';

import type { ClientId } from '@nesso/ledger-contracts/ids';

import { queryKeys as paymentQueryKeys } from '@/entities/payment';
import { formatRateForInput } from '@/entities/service-catalog';
import {
    type SessionCharge,
    queryKeys as sessionChargeQueryKeys,
    useChangeFeeOptions,
} from '@/entities/session-charge';
import { ApiError } from '@/shared/api/http-client';

import {
    type EditSessionFeeFormOutput,
    type EditSessionFeeFormValues,
    editSessionFeeFormSchema,
} from './edit-session-fee-form.schema';

type Options = {
    clientId: ClientId;
    sessionCharge: SessionCharge;
    onSuccess: () => void;
};

export function useEditSessionFeeForm({ clientId, sessionCharge, onSuccess }: Options) {
    const queryClient = useQueryClient();
    const changeFeeMutation = useMutation({
        ...useChangeFeeOptions(),
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: paymentQueryKeys.all });
        },
        onError: () => {
            void queryClient.invalidateQueries({ queryKey: sessionChargeQueryKeys.all });
        },
    });

    const form = useForm<EditSessionFeeFormValues, unknown, EditSessionFeeFormOutput>({
        resolver: zodResolver(editSessionFeeFormSchema),
        defaultValues: {
            serviceCode: sessionCharge.serviceCode,
            fee: formatRateForInput(sessionCharge.feeCents),
        },
    });

    const { isSubmitted } = form.formState;

    // Fee follows the newly chosen service's rate only when the choice actually changes the
    // service — reselecting the one already picked leaves a typed amount alone.
    const selectService = (code: string, rateCents: number) => {
        const changed = code !== form.getValues('serviceCode');
        form.setValue('serviceCode', code, { shouldValidate: isSubmitted });
        if (changed) {
            form.setValue('fee', formatRateForInput(rateCents), { shouldValidate: isSubmitted });
        }
    };

    const onSubmit = form.handleSubmit((data) => {
        if (
            data.serviceCode === sessionCharge.serviceCode &&
            data.feeCents === sessionCharge.feeCents
        ) {
            onSuccess();
            return;
        }

        changeFeeMutation.mutate(
            {
                id: sessionCharge.id,
                clientId,
                serviceCode: data.serviceCode,
                feeCents: data.feeCents,
                expected: {
                    serviceCode: sessionCharge.serviceCode,
                    feeCents: sessionCharge.feeCents,
                    paidCents: sessionCharge.paidCents,
                },
            },
            {
                onSuccess,
                onError: (error) => {
                    if (
                        error instanceof ApiError &&
                        error.status === 409 &&
                        error.message === 'SESSION_CHANGED'
                    ) {
                        form.setError('root', {
                            message: 'This session has changed. Close and open Edit Fee again.',
                        });
                        return;
                    }
                    form.setError('root', { message: "Couldn't save the fee. Try again." });
                },
            },
        );
    });

    return {
        form,
        onSubmit,
        selectService,
        isPending: changeFeeMutation.isPending,
    };
}
